
/**
 * Module dependencies.
 */

import { color, units } from 'src/styles/utils';
import { ifProp, switchProp, theme } from 'styled-tools';
import { isExternalRoute, isScriptRoute } from 'src/core/utils/routes';
import { setFontStyle } from 'src/styles/utils/typography';
import { typography } from 'src/styles/type';
import React, { ElementType, ReactElement, forwardRef } from 'react';
import RouterLink from 'src/components/core/links/router-link';
import Svg from 'src/components/core/svg';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  as?: ElementType,
  className?: string,
  colorTheme?: 'black' | 'white',
  disabled?: boolean,
  href?: string,
  icon: string,
  iconSize: string,
  label?: string,
  labelAlign?: 'end' | 'start',
  locale?: string,
  onClick?: () => void,
};

/**
 * `Label` styled component.
 *
 * Tooltip shown on hover/focus. Anchored to one of the button's edges
 * (`labelAlign`) instead of centered, so it is not clipped by the slider's
 * `overflow: hidden` when the button sits near the edge of a card.
 */

const Label = styled.span<Pick<Props, 'labelAlign'>>`
  ${setFontStyle(typography.typeSizes.small)}

  background-color: ${color('grey900')};
  bottom: calc(100% + ${units(1)});
  color: ${color('white')};
  opacity: 0;
  padding: ${units(0.5)} ${units(1)};
  pointer-events: none;
  position: absolute;
  transform: translateY(${units(0.5)});
  transition: ${theme('animations.defaultTransition')};
  transition-property: opacity, transform;
  visibility: hidden;
  white-space: nowrap;
  z-index: 1;

  ${switchProp('labelAlign', {
    end: css`
      right: 0;
    `,
    start: css`
      left: 0;
    `
  }, css`
    left: 0;
  `)}
`;

/**
 * `Button` styled component.
 */

const Button = styled.button.attrs<Props>(({ as, colorTheme, href, type }) => {
  const isAnchor = isExternalRoute(href) || isScriptRoute(href);
  const element = as || href && !isAnchor && RouterLink || href && isAnchor && 'a' || 'button';

  return {
    as: element,
    colorTheme: colorTheme || 'black',
    type: type || (element === 'button' ? 'button' : null)
  };
})`
  -webkit-tap-highlight-color: transparent;
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  grid-area: closeButton;
  outline: none;
  padding: 0;
  position: relative;
  transition: color ${theme('animations.defaultTransition')};

  ${switchProp('colorTheme', {
    black: css`
      color: ${color('textColor')};

      &:focus,
      &:hover {
        color: ${color('grey300')};
      }
    `,
    white: css`
      color: ${color('white')};

      &:focus,
      &:hover {
        color: ${color('grey200')};
      }
    `
  })}

  &:focus-visible ${Label},
  &:hover ${Label} {
    opacity: 1;
    transform: translateY(0);
    visibility: visible;
  }

  ${ifProp('disabled', css`
    color: ${color('grey600')};
    cursor: default;
    pointer-events: none;
  `)}
`;

/**
 * `IconButton` component.
 */

const IconButton = (props: Props, ref: any): ReactElement => {
  const { icon, iconSize, label, labelAlign, ...rest } = props;

  return (
    <Button
      {...rest}
      ref={ref}
    >
      <Svg
        icon={icon}
        size={iconSize}
      />

      {label && (
        <Label
          aria-hidden
          labelAlign={labelAlign}
        >
          {label}
        </Label>
      )}
    </Button>
  );
};

/**
 * Export `IconButton` component.
 */

export default forwardRef<Props, any>(IconButton);
